import * as React from "react";

import { convertToMarkdown } from "./helpers";

/**
 * Returns an `onCopy` handler that converts the user's HTML selection back
 * to markdown before writing it to the clipboard. Used by both the shared
 * {@link RobotoMarkdown} and chat's `ChatResponseMarkdown` components so
 * the clipboard format stays consistent regardless of which renderer the
 * user happens to be copying from.
 */
export function useMarkdownClipboardCopy(): React.ClipboardEventHandler<HTMLElement> {
  return React.useCallback((e) => {
    e.preventDefault();

    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    // Single-element selections: copy the parent element's full outerHTML
    // so its semantic markup (link, code, list item, …) survives the
    // round-trip rather than collapsing to plain text.
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let commonAncestor: Node | HTMLElement | null =
        range.commonAncestorContainer;

      if (commonAncestor.nodeType === Node.TEXT_NODE) {
        commonAncestor = commonAncestor.parentElement;
      }

      const selectedContent = range.cloneContents();

      if (
        commonAncestor &&
        commonAncestor?.textContent?.trim() === selection.toString().trim() &&
        selectedContent.childNodes.length === 1
      ) {
        const anchorNode = selection.anchorNode;
        const parentElement = anchorNode?.parentElement;
        const htmlString = parentElement?.outerHTML ?? "";

        void navigator.clipboard.writeText(convertToMarkdown(htmlString));

        return;
      }
    }

    // Multi-element selections: clone every range into a detached container
    // and serialize together.
    const container = document.createElement("div");
    for (let i = 0; i < selection.rangeCount; i++) {
      container.appendChild(selection.getRangeAt(i).cloneContents());
    }

    void navigator.clipboard.writeText(convertToMarkdown(container.innerHTML));
  }, []);
}
